import { format, startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";

import type { CreateMeetingRequestJson, ExtendedWorkspace } from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";

import { useAddWorkspaceMeeting } from "../../../../../../api/hooks";
import useForm from "../../../../../../hooks/useForm";
import { useCalendarErrorHandler, useValidationErrorClearer } from "../../../hooks";

import { getDefaultMeetingTimes } from "../../../utils";

import { MeetingFormSchema, formSchema } from "../../../schema";
import MeetingForm, { MeetingFormData } from "../MeetingForm";

// Keep default values outside component to prevent recreation on each render
const getDefaultFormValues = (): MeetingFormSchema => {
	const defaultTimes = getDefaultMeetingTimes();
	return {
		name: "",
		startDate: startOfDay(defaultTimes.startDateTime),
		endDate: startOfDay(defaultTimes.endDateTime),
		startTime: format(defaultTimes.startDateTime, "HH:mm"),
		endTime: format(defaultTimes.endDateTime, "HH:mm"),
		description: "",
		carryOver: false,
		addToCalendar: false,
	};
};

interface CreateMeetingModalProps {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace;
	workspaceHasMeetings: boolean;
	setExpandedMeetingIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export const CreateMeetingModal = ({
	isOpen,
	handleClose,
	workspace,
	workspaceHasMeetings,
	setExpandedMeetingIds,
}: CreateMeetingModalProps) => {
	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof MeetingFormSchema, string>>>({});

	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();

	const { mutate: addWorkspaceMeeting, isPending } = useAddWorkspaceMeeting(
		handleClose,
		setExpandedMeetingIds,
		(error, variables) => {
			// Create calendar error context for the centralized handler
			const context = {
				operation: "create" as const,
				meetingData: variables.data,
				workspaceId: variables.workspaceId,
			};
			return handleCalendarError(error, context);
		}
	);

	const {
		validate,
		updateForm,
		state: formValues,
	} = useForm({
		schema: formSchema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			updateForm(getDefaultFormValues());
			setValidationErrors({});
		}
	}, [isOpen]);

	// Custom hook for clearing validation errors when form values change
	useValidationErrorClearer(formValues, validationErrors, setValidationErrors);

	const handleSubmit = (data: MeetingFormData) => {
		// Validate form
		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors = Object.entries(validationResult.errors || {}).reduce(
				(acc, [key, error]) => {
					acc[key as keyof MeetingFormSchema] = error.message;
					return acc;
				},
				{} as Partial<Record<keyof MeetingFormSchema, string>>
			);
			setValidationErrors(errors);
			return;
		}

		const meetingParticipants = workspace.participants?.map((participant) => ({
			userId: participant.userId,
			role: participant.role,
		}));

		const formattedData: CreateMeetingRequestJson = {
			...data,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
			participants: meetingParticipants,
		};

		addWorkspaceMeeting({ workspaceId: workspace._id, data: formattedData });
	};

	// Reset form and close modal
	const onClose = () => {
		// If calendar error modal is open, just close it without calling handleClose
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}

		handleClose();
		setValidationErrors({});
	};

	return (
		<>
			{/* Only show MeetingForm when calendar error modal is not open */}
			{calendarModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValues}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Create Meeting"
					isPending={isPending}
					title="Create a New Meeting"
					showCarryOver={workspaceHasMeetings}
					showCalendarIntegration={true}
					workspaceId={workspace._id}
				/>
			)}

			<CalendarErrorModal
				modalState={calendarModalState}
				onRetry={() => {
					if (calendarModalState.type === "error") {
						setRetrying(true);
						const { context } = calendarModalState;
						if (context.operation === "create") {
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: context.meetingData },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (calendarModalState.type === "error") {
						const { context } = calendarModalState;
						if (context.operation === "create") {
							// For create operations, continue without calendar means create without addToCalendar
							const dataWithoutCalendar = { ...context.meetingData, addToCalendar: false };

							setRetrying(true);
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: dataWithoutCalendar },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onClose={closeModal}
			/>
		</>
	);
};
